import { FC, RefObject, useEffect, useMemo, useRef, MouseEvent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';

// components
import { PdpRespSimilarItemCarousel } from './PdpRespSimilarItemCarousel/PdpRespSimilarItemCarousel';
import { PdpRespSimilarItemsContainer } from './PdpRespSimilarItemsContainer/PdpRespSimilarItemsContainer';
import { viewSimilarItems, ItemsSimilarToHeader } from '../pdpMessages';
import { PdpSharedUnpurchasableHeader } from '../PdpSharedUnpurchasableIcons/PdpSharedUnpurchasableHeader';

import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { useClientState } from 'dibs-react-hooks/exports/useClientState';
import {
    VisibilityTracker,
    VisibilityChangeCallback,
} from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { FavoritesProviderChildrenProps } from '../../components/global/FavoriteIcons/FavoritesProviderChildrenProps';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';

import { locations } from '../../utils/tracking/shared/favoritesTracking';

// actions
import { hasFiredLandingItemsImpressions } from '../../actions/pdpRespSimilarItemsActions';
import { useClickabilityTracking } from '../tracking/useClickabilityTracking';

// helpers
import classnames from 'classnames';
import scrollIntoView from 'scroll-into-view';
import {
    trackItemClick,
    trackEcommerceLandingItemClick,
    trackLandingViewAllClick,
    trackLandingItemImpressions,
    trackItemsDisplayed,
} from '../tracking/similarItemsTracking';

import { PdpRespSimilarItemsLanding_item$key } from './__generated__/PdpRespSimilarItemsLanding_item.graphql';
import {
    PdpRespSimilarItemsLanding_similarItems$data,
    PdpRespSimilarItemsLanding_similarItems$key,
} from './__generated__/PdpRespSimilarItemsLanding_similarItems.graphql';
import { PdpRespSimilarItemsLanding_user$key } from './__generated__/PdpRespSimilarItemsLanding_user.graphql';

// styles
import styles from './PdpRespSimilarItemsLanding.scss';

// types
type PdpRespSimilarItemsLandingComponentProps = {
    item: PdpRespSimilarItemsLanding_item$key;
    similarItems: PdpRespSimilarItemsLanding_similarItems$key;
    user: PdpRespSimilarItemsLanding_user$key;
    scrollToRef: RefObject<HTMLElement>;
    landingItemSize?: string;
};

type LandingItems = NonNullable<
    NonNullable<PdpRespSimilarItemsLanding_similarItems$data['edges']>[number]
>;

type SelectorState = {
    similarItems?: {
        ecommerceImpressions?: {
            hasFiredLandingItems?: boolean;
        };
    };
};

type SelectorResponse = {
    hasFiredImpressions: boolean;
};

// constants
const PURCHASABLE_ITEM_COUNT = 6;

// GraphQL Fragments
const userFragment = graphql`
    fragment PdpRespSimilarItemsLanding_user on User {
        serviceId
    }
`;

const itemFragment = graphql`
    fragment PdpRespSimilarItemsLanding_item on Item {
        title
        isSold
        isListingNotListed
        isListingUnlisted
        seller {
            isInternal
            isAccessible
        }
        ...PdpSharedUnpurchasableHeader_item
        ...PdpRespSimilarItemsContainer_item
    }
`;

const similarItemsFragment = graphql`
    fragment PdpRespSimilarItemsLanding_similarItems on moreLikeThisItemConnection {
        edges {
            node {
                serviceId
                categoryPath
                categoryCode
                browseUrl
                title
                ecommerceTrackingParams
                ...PdpRespSimilarItemCarousel_item
            }
        }
    }
`;

export const PdpRespSimilarItemsLanding: FC<PdpRespSimilarItemsLandingComponentProps> = ({
    item: itemRef,
    similarItems: similarItemsRef,
    user: userRef,
    scrollToRef,
    landingItemSize,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const similarItems = useFragment(similarItemsFragment, similarItemsRef);
    const user = useFragment(userFragment, userRef);

    const relayEnvironment = useRelayEnvironment();
    const sectionVisibilityRef = useRef(null);
    const isClient = useClientState();

    const dispatch = useDispatch();
    const hasFiredImpressions = useSelector<SelectorState, boolean>(
        state => state?.similarItems?.ecommerceImpressions?.hasFiredLandingItems || false
    );

    const handleSectionVisibilityChange = ({
        isVisible,
    }: Pick<VisibilityChangeCallback, 'isVisible'>): void => {
        if (isVisible) {
            trackItemsDisplayed({ moduleType: 'PDP_SIMILAR_ITEMS' });
        }
    };

    const scrollToPdpRespSimilarItemsGrid = (): void => {
        const gridEl = scrollToRef?.current;
        if (gridEl) {
            scrollIntoView(gridEl, { time: 500 }, () => gridEl.focus());
        }
    };

    const isListingNotListed = item.isListingNotListed && !item?.seller?.isInternal;
    const isPdpTypeDisplayUnpurchasable =
        ((item.isListingUnlisted || item.isSold) && !isListingNotListed) ||
        !item?.seller?.isAccessible;

    const landingItems = useMemo<LandingItems[]>(() => {
        const items = similarItems?.edges ? similarItems.edges.filter(filterNulls) : [];
        return isPdpTypeDisplayUnpurchasable ? items : items.slice(0, PURCHASABLE_ITEM_COUNT);
    }, [similarItems, isPdpTypeDisplayUnpurchasable]);

    const onClickItem = (index: number, event: MouseEvent | null = null): void => {
        trackItemClick({ moduleType: 'PDP_SIMILAR_ITEMS' }, event as $TSFixMe);
        trackEcommerceLandingItemClick({
            index,
            landingItem: landingItems[index],
            isSoldPdp: isPdpTypeDisplayUnpurchasable,
        });
    };

    const onClickViewMore = (event?: MouseEvent | null): void => {
        scrollToPdpRespSimilarItemsGrid();

        // fire tracking
        trackLandingViewAllClick(event as $TSFixMe);
    };

    useClickabilityTracking();

    const ecommerceTrackingParams = similarItems?.edges?.[0]?.node?.ecommerceTrackingParams;
    useEffect(() => {
        if (!hasFiredImpressions && ecommerceTrackingParams) {
            if (landingItems.length) {
                trackLandingItemImpressions(landingItems, isPdpTypeDisplayUnpurchasable);
                dispatch(hasFiredLandingItemsImpressions());
            }
        }
    }, [
        hasFiredImpressions,
        ecommerceTrackingParams,
        landingItems,
        isPdpTypeDisplayUnpurchasable,
        dispatch,
    ]);

    const itemTitle = <i>{item.title}</i>;

    let initialLandingItems = landingItems;
    let additionalLandingItems: undefined | LandingItems[] = undefined;

    let header: JSX.Element | undefined = undefined;
    let subtitle: JSX.Element;
    let isHorizontal = false;

    if (isPdpTypeDisplayUnpurchasable) {
        // Unpurchasable Pdp
        header = <PdpSharedUnpurchasableHeader item={item} />;
        subtitle = viewSimilarItems;
    } else {
        // GROWTH-1429: Test Similar Items on Pdp
        subtitle = <ItemsSimilarToHeader title={itemTitle} />;
        isHorizontal = true;
    }

    if (initialLandingItems.length > PURCHASABLE_ITEM_COUNT) {
        additionalLandingItems = initialLandingItems.slice(PURCHASABLE_ITEM_COUNT);
        initialLandingItems = initialLandingItems.slice(0, PURCHASABLE_ITEM_COUNT);
    }

    const itemProps = {
        onClick: onClickItem,
        isCondensed: isHorizontal,
    };

    const landingItemsClassNames = classnames(styles.landingItems, 'rowFlex', {
        [styles.isHorizontal]: isHorizontal,
    });

    const landingItemClassNames = classnames('colLg2', 'colMd4', 'colXs6', styles.landingItem, {
        [styles.smallLandingItem]: landingItemSize === 'small',
    });

    return (
        <div ref={sectionVisibilityRef}>
            <VisibilityTracker
                elementRef={sectionVisibilityRef}
                onVisibilityChange={handleSectionVisibilityChange}
            />
            <FavoritesProvider
                location={locations.SIMILAR_ITEM_TOP}
                disable={!isClient}
                userId={user?.serviceId || null}
                environment={relayEnvironment}
                itemIds={
                    similarItems?.edges?.map(edge => edge?.node?.serviceId).filter(filterNulls) ||
                    []
                }
                fetchFolder={false}
            >
                {({ props: favorites }: { props: FavoritesProviderChildrenProps }) => (
                    <PdpRespSimilarItemsContainer
                        isDark
                        isTopContainer
                        header={header}
                        subtitle={subtitle}
                        onClickViewMore={onClickViewMore}
                        dataTn="pdp-similar-landing-grid"
                        isHorizontal={isHorizontal}
                        item={item}
                    >
                        {/* Grid of Items */}
                        {!initialLandingItems.length ? null : (
                            <>
                                <div className={landingItemsClassNames}>
                                    {initialLandingItems.map(({ node }, index) => (
                                        <div
                                            key={node?.serviceId}
                                            className={landingItemClassNames}
                                        >
                                            <PdpRespSimilarItemCarousel
                                                item={node}
                                                index={index}
                                                favorites={favorites}
                                                {...itemProps}
                                            />
                                        </div>
                                    ))}
                                </div>
                                {additionalLandingItems ? (
                                    <div className={landingItemsClassNames}>
                                        {additionalLandingItems.map(({ node }, index) => (
                                            <div
                                                key={node?.serviceId}
                                                className={landingItemClassNames}
                                            >
                                                <PdpRespSimilarItemCarousel
                                                    item={node}
                                                    index={index + 6}
                                                    favorites={favorites}
                                                    {...itemProps}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </>
                        )}
                    </PdpRespSimilarItemsContainer>
                )}
            </FavoritesProvider>
        </div>
    );
};
